import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, AlertTriangle, Loader2, Check, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { patchInsurance } from "@/lib/noteClaim";
import { PayerPicker } from "./ManageInsuranceModal";

const HAIR = "#E9E9EC", INK = "#141412", GREY = "#6E6E6B", SUB = "#6C6C69", INDIGO = "#5856D6";

// True when a carrier name is known on the primary but its Availity id is missing/unresolved.
export const needsPayerId = (n) => { const p = n?.insurance; return !!(p?.payer_name && (p.needs_review || p.payer_id_missing || !p.payer_id || p.payer_id === "MISSING")); };

function Group({ g, clientId, onApplied }) {
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { ok, failed }

  const apply = async () => {
    if (!picked?.payer_id) { toast.error("Pick the correct payer first."); return; }
    setBusy(true);
    let ok = 0; const failed = [];
    for (const n of g.notes) {
      try { await patchInsurance(n.id, { payer_id: picked.payer_id, payer_name: picked.payer_name, sequence: 1, teach_alias: g.name }); ok += 1; }
      catch { failed.push(n.id); }
    }
    setBusy(false); setDone({ ok, failed: failed.length, payer: picked.payer_name });
    if (ok) toast.success(`Linked ${picked.payer_name} to ${ok} visit${ok === 1 ? "" : "s"}${failed.length ? ` · ${failed.length} failed` : ""}`);
    else toast.error("Couldn't link these visits.");
    onApplied?.(g, ok);
  };

  return (
    <div className="rounded-[12px] border bg-white p-3.5" style={{ borderColor: HAIR }} data-testid={`npi-group-${g.key}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: INK }}>
          <span className="w-2 h-2 rounded-full" style={{ background: done ? "#1F9D57" : "#C22219" }} />{g.name}
        </span>
        <span className="text-[11.5px]" style={{ color: GREY }}>· {g.notes.length} visit{g.notes.length === 1 ? "" : "s"} missing a payer id</span>
      </div>

      {done ? (
        <div className="text-[12.5px] inline-flex items-center gap-1.5" style={{ color: "#1F9D57" }} data-testid={`npi-group-done-${g.key}`}>
          <CheckCircle2 size={14} />Linked to {done.payer} — {done.ok} updated{done.failed ? ` · ${done.failed} failed` : ""}.
        </div>
      ) : (
        <>
          <div className="text-[11.5px] mb-2 flex flex-wrap gap-x-3 gap-y-0.5" style={{ color: SUB }}>
            {g.notes.slice(0, 6).map((n) => <span key={n.id}>{n.patient_name || n.mrn || n.visit_id}{n.date_of_service ? ` · ${n.date_of_service}` : ""}</span>)}
            {g.notes.length > 6 ? <span style={{ color: GREY }}>+{g.notes.length - 6} more</span> : null}
          </div>
          <PayerPicker seq={g.key} clientId={clientId} onPick={setPicked} />
          {picked ? <div className="text-[11.5px] mt-1.5" style={{ color: INDIGO }} data-testid={`npi-picked-${g.key}`}>Apply <b>{picked.payer_name}</b> · {picked.payer_id} to all {g.notes.length}</div> : null}
          <div className="flex items-center justify-end mt-2.5">
            <button onClick={apply} disabled={busy || !picked} className="text-[12px] font-bold text-white rounded-[8px] px-3.5 py-2 inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: INK }} data-testid={`npi-apply-${g.key}`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}Apply to {g.notes.length} visit{g.notes.length === 1 ? "" : "s"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Groups every "needs payer id" visit by the carrier name read from the document so staff can
// set the real Availity id once and sweep it across all visits with that same payer, even across gaps.
export default function NeedsPayerIdModal({ notes, clientId, onClose, onApplied }) {
  const groups = useMemo(() => {
    const m = new Map();
    (notes || []).forEach((n) => {
      const name = (n.insurance?.payer_name || "Unknown payer").trim();
      const key = name.toLowerCase();
      if (!m.has(key)) m.set(key, { key, name, notes: [] });
      m.get(key).notes.push(n);
    });
    return [...m.values()].sort((a, b) => b.notes.length - a.notes.length);
  }, [notes]);
  const total = (notes || []).length;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(20,20,18,.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-white rounded-[18px] overflow-hidden flex flex-col" style={{ width: "min(620px,96vw)", maxHeight: "88vh", boxShadow: "0 28px 80px rgba(0,0,0,.3)" }} role="dialog" data-testid="needs-payerid-modal">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b flex-none" style={{ borderColor: HAIR }}>
          <span className="w-8 h-8 rounded-[9px] grid place-items-center flex-none bg-[#FBE9E7] text-[#C22219]"><AlertTriangle size={16} /></span>
          <div className="min-w-0">
            <div className="text-[15px] font-bold" style={{ color: INK }}>Payer ids needed</div>
            <div className="text-[11.5px]" style={{ color: GREY }} data-testid="npi-summary">{total} visit{total === 1 ? "" : "s"} across {groups.length} payer{groups.length === 1 ? "" : "s"} — set the Availity id once per payer</div>
          </div>
          <span className="flex-1" />
          <button onClick={onClose} className="w-8 h-8 rounded-[8px] grid place-items-center" style={{ color: GREY }} data-testid="npi-close"><X size={17} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-3" style={{ background: "#F7F7F9" }}>
          {!total ? (
            <div className="text-[13px] py-10 text-center" style={{ color: GREY }} data-testid="npi-empty">Every visit with a carrier already has a payer id. Nothing to fix.</div>
          ) : groups.map((g) => <Group key={g.key} g={g} clientId={clientId} onApplied={onApplied} />)}
        </div>
      </div>
    </div>, document.body);
}
